/**
 * SettingsPanel — Mission Control settings modal.
 *
 * Props: { open, onClose, cockpitMode, onToggleCockpit }
 *
 * Features:
 *   - Theme toggle: Cockpit (dark) ↔ Paper (light)
 *   - Keyboard shortcut reference
 *   - Escape + backdrop click to close
 *
 * Design: follows ShortcutsOverlay pattern — centered modal, Fraunces title,
 * DM Sans body, warm-grey tokens per Xpert design system.
 */
import { useEffect } from "react";
import { Moon, Sun, X } from "lucide-react";

// ── Inline styles (self-contained, no mc.css namespace pollution) ─────────────

const S = {
  backdrop: {
    position:        "fixed",
    inset:           0,
    backgroundColor: "rgba(0,0,0,0.48)",
    backdropFilter:  "blur(2px)",
    zIndex:          210,
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
  },
  modal: {
    width:           "100%",
    maxWidth:        440,
    backgroundColor: "var(--mc-bg)",
    border:          "1px solid var(--mc-border)",
    borderRadius:    8,
    overflow:        "hidden",
    boxShadow:       "0 24px 80px -30px rgba(14,20,16,0.55)",
  },
  header: {
    height:       52,
    display:      "flex",
    alignItems:   "center",
    padding:      "0 20px",
    borderBottom: "1px solid var(--mc-border)",
    flexShrink:   0,
    gap:          12,
  },
  headerTitle: {
    flex:       1,
    fontFamily: "var(--font-display)",
    fontSize:   18,
    fontWeight: 500,
    color:      "var(--mc-text)",
    margin:     0,
    letterSpacing: "-0.01em",
  },
  closeBtn: {
    background:  "transparent",
    border:      "none",
    cursor:      "pointer",
    color:       "var(--color-slate-400)",
    display:     "flex",
    alignItems:  "center",
    padding:     4,
    borderRadius: 4,
    transition:  "color 120ms",
    lineHeight:  1,
  },
  body: {
    padding:    "8px 0 0",
  },
  sectionLabel: (isFirst) => ({
    fontFamily:    "var(--font-mono)",
    fontSize:      10,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color:         "var(--color-slate-500)",
    padding:       "10px 20px 6px",
    borderTop:     isFirst ? "none" : "1px solid var(--mc-border)",
    margin:        0,
  }),
  row: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "10px 20px",
    gap:            16,
  },
  rowLeft: {
    display:       "flex",
    flexDirection: "column",
    gap:           2,
    flex:          1,
  },
  rowTitle: {
    fontFamily:  "var(--font-sans)",
    fontSize:    13,
    fontWeight:  500,
    color:       "var(--mc-text)",
    margin:      0,
  },
  rowDesc: {
    fontFamily:  "var(--font-sans)",
    fontSize:    11,
    color:       "var(--color-slate-500)",
    margin:      0,
  },
  themeToggle: {
    display:      "flex",
    border:       "1px solid var(--mc-border)",
    borderRadius: 6,
    overflow:     "hidden",
    flexShrink:   0,
  },
  themeBtn: (active) => ({
    display:     "inline-flex",
    alignItems:  "center",
    gap:         5,
    padding:     "5px 12px",
    fontSize:    12,
    fontFamily:  "var(--font-sans)",
    fontWeight:  500,
    background:  active ? "var(--color-green)" : "transparent",
    color:       active ? "#fff" : "var(--color-slate-400)",
    border:      "none",
    cursor:      active ? "default" : "pointer",
    transition:  "background 150ms, color 150ms",
    lineHeight:  "18px",
  }),
  shortcutRow: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "8px 20px",
  },
  shortcutLabel: {
    fontFamily: "var(--font-sans)",
    fontSize:   12,
    color:      "var(--color-slate-400)",
    margin:     0,
  },
  kbdGroup: {
    display: "flex",
    gap:     4,
  },
  kbd: {
    display:       "inline-flex",
    alignItems:    "center",
    justifyContent:"center",
    minWidth:      20,
    height:        20,
    padding:       "0 5px",
    fontFamily:    "var(--font-mono)",
    fontSize:      10,
    fontWeight:    500,
    color:         "var(--mc-text)",
    background:    "var(--mc-surface)",
    border:        "1px solid var(--mc-border)",
    borderBottom:  "2px solid var(--mc-border)",
    borderRadius:  3,
  },
  footer: {
    borderTop:   "1px solid var(--mc-border)",
    padding:     "10px 20px",
    fontFamily:  "var(--font-mono)",
    fontSize:    10,
    color:       "var(--color-slate-600)",
    display:     "flex",
    justifyContent: "space-between",
    alignItems:  "center",
  },
};

// ── Keyboard shortcuts (read-only reference) ──────────────────────────────────

const SHORTCUTS = [
  { keys: ["⌘", "."],      label: "Toggle cockpit / paper mode" },
  { keys: ["⌘", "K"],      label: "Command palette"             },
  { keys: ["?"],           label: "Keyboard shortcuts"           },
  { keys: ["⌘", "⇧", "!"], label: "I am driving (pause agents)" },
];

// ── SettingsPanel ─────────────────────────────────────────────────────────────

export default function SettingsPanel({ open, onClose, cockpitMode, onToggleCockpit }) {
  // Escape to close
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={S.backdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        style={S.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Mission Control settings"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={S.header}>
          <h2 style={S.headerTitle}>Settings</h2>
          <button
            style={S.closeBtn}
            onClick={onClose}
            aria-label="Close settings"
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--mc-text)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-slate-400)")}
          >
            <X size={16} />
          </button>
        </div>

        <div style={S.body}>
          {/* ── Display section ── */}
          <p style={S.sectionLabel(true)}>Display</p>

          <div style={S.row}>
            <div style={S.rowLeft}>
              <p style={S.rowTitle}>Theme</p>
              <p style={S.rowDesc}>Dark cockpit or light paper surface</p>
            </div>

            {/* Segmented theme toggle */}
            <div style={S.themeToggle} role="group" aria-label="Theme">
              <button
                style={S.themeBtn(cockpitMode)}
                onClick={() => { if (!cockpitMode) onToggleCockpit(); }}
                aria-pressed={cockpitMode}
                title="Cockpit (dark)"
              >
                <Moon size={12} aria-hidden="true" />
                Cockpit
              </button>
              <button
                style={{
                  ...S.themeBtn(!cockpitMode),
                  borderLeft: "1px solid var(--mc-border)",
                }}
                onClick={() => { if (cockpitMode) onToggleCockpit(); }}
                aria-pressed={!cockpitMode}
                title="Paper (light)"
              >
                <Sun size={12} aria-hidden="true" />
                Paper
              </button>
            </div>
          </div>

          {/* ── Keyboard section ── */}
          <p style={S.sectionLabel(false)}>Keyboard shortcuts</p>

          {SHORTCUTS.map(({ keys, label }) => (
            <div key={label} style={S.shortcutRow}>
              <span style={S.shortcutLabel}>{label}</span>
              <span style={S.kbdGroup}>
                {keys.map((k) => (
                  <kbd key={k} style={S.kbd}>{k}</kbd>
                ))}
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <span>Mission Control</span>
          <span>
            {new Date().toLocaleDateString("en-GB", {
              timeZone: "Asia/Dubai",
              day: "2-digit",
              month: "short",
              year: "numeric",
            })} GST
          </span>
        </div>
      </div>
    </div>
  );
}
